const MAX_IMAGE_PIXELS = 64 * 1024 * 1024;

export async function prepareMetadataAvatar(content: Uint8Array, contentType: string) {
  const blob = new Blob([new Uint8Array(content)], { type: contentType });
  let source: ImageBitmap | HTMLImageElement;
  let objectUrl: string | undefined;
  if (typeof OffscreenCanvas !== 'undefined' && typeof createImageBitmap === 'function') {
    source = await createImageBitmap(blob);
  } else {
    objectUrl = URL.createObjectURL(blob);
    const image = new Image();
    try {
      await new Promise<void>((resolve, reject) => {
        image.onload = () => resolve();
        image.onerror = () => reject(new Error('InvalidMetadataAvatar'));
        image.src = objectUrl!;
      });
      source = image;
    } catch (error) {
      URL.revokeObjectURL(objectUrl);
      throw error;
    }
  }
  try {
    const width = 'naturalWidth' in source ? source.naturalWidth : source.width;
    const height = 'naturalHeight' in source ? source.naturalHeight : source.height;
    if (!width || !height || width * height > MAX_IMAGE_PIXELS) throw new Error('InvalidMetadataAvatar');
    const resize = async (size: number) => {
      const canvas = typeof OffscreenCanvas !== 'undefined'
        ? new OffscreenCanvas(size, size) : document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const context = canvas.getContext('2d') as CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;
      if (!context) throw new Error('MetadataCanvasUnavailable');
      const crop = Math.min(width, height);
      context.drawImage(source, (width - crop) / 2, (height - crop) / 2, crop, crop, 0, 0, size, size);
      const encode = (type: string) => 'convertToBlob' in canvas
        ? canvas.convertToBlob({ type, quality: 0.85 })
        : new Promise<Blob>((resolve, reject) => {
          canvas.toBlob((value) => value ? resolve(value) : reject(new Error('MetadataEncodingFailed')),
            type, 0.85);
        });
      let output: Blob | undefined;
      try {
        output = await encode('image/webp');
      } catch {
        // Some Safari canvas implementations reject WebP encoding altogether.
      }
      if (!output?.size || !['image/webp', 'image/png'].includes(output.type)) {
        output = await encode('image/png');
      }
      if ((output.type !== 'image/webp' && output.type !== 'image/png') || !output.size) {
        throw new Error('MetadataEncodingFailed');
      }
      return new Uint8Array(await output.arrayBuffer());
    };
    return { small: await resize(96), large: await resize(384) };
  } finally {
    if ('close' in source) source.close();
    if (objectUrl) URL.revokeObjectURL(objectUrl);
  }
}
